/**
 * Permission codes the party, consent and DSR screens declare an interest in
 * (`internal/party/permissions.go`, design C5 §6.1).
 *
 * Naming them once here means a renamed permission is a compile error rather
 * than a menu that silently disappears.
 *
 * **Two of these are MFA step-up guarded server-side** —
 * `party.identifier.reveal` and `party.dsr.erase`. The screens treat
 * `ERR_STEP_UP_REQUIRED` as a guided step rather than a refusal; `@/api/client`
 * answers the challenge and replays the request, so no screen here contains a
 * word about it.
 *
 * **`party.identifier.reveal` and `party.dsr.export` are acts, not reads.**
 * `readOnlyCodes` is built by a filter over action names, and these two are the
 * C5 codes whose action is read-shaped and whose effect is not: revealing a
 * passport number and exporting a personal-data bundle both move data out of the
 * system. An auditor holds neither, which is why the DSR screen offers the
 * download only to `party.dsr.export` and shows the server's **403** — not a
 * 404 — when it is missing (§6.11 rule 2: the request is legitimately visible,
 * only the artefact is not, and a 404 here would be a lie the operator has to
 * debug).
 *
 * **`internal/party`'s routes declare `Module: ""`** — no entitlement gate.
 * Answering a data-subject request is a compliance obligation, not a paid
 * add-on. The **loyalty** routes are the exception and carry `Module: "crm"`,
 * which is why their codes live under `crm.loyalty.*` below.
 */

export const PARTY_PERMISSIONS = {
  read: 'party.read',
  manage: 'party.manage',
  /** Decrypting an identifier. MFA step-up; writes `ActionSensitiveReveal`. */
  reveal: 'party.identifier.reveal',

  mergePropose: 'party.merge.propose',
  mergeApprove: 'party.merge.approve',
  mergeReverse: 'party.merge.reverse',

  consentRead: 'party.consent.read',
  consentCapture: 'party.consent.capture',
  consentManage: 'party.consent.manage',

  suppressionRead: 'party.suppression.read',
  suppressionManage: 'party.suppression.manage',

  dsrRead: 'party.dsr.read',
  dsrManage: 'party.dsr.manage',
  /** Build and download an export bundle. Its own `ActionExport` audit row. */
  dsrExport: 'party.dsr.export',
  /** Execute an erasure. MFA step-up. Absolutely refused unverified. */
  dsrErase: 'party.dsr.erase',

  retentionRead: 'party.retention.read',
  retentionManage: 'party.retention.manage',

  /** Loyalty is commercial: `Module: "crm"`. */
  loyaltyRead: 'crm.loyalty.read',
  loyaltyManage: 'crm.loyalty.manage',

  /** Read companies for the merge-settings picker. */
  companyRead: 'org.company.read',
} as const

export type PartyPermission = (typeof PARTY_PERMISSIONS)[keyof typeof PARTY_PERMISSIONS]

/**
 * The three declared segregation-of-duties pairs (§6.1, wiring block item F).
 *
 * Listed for the screens that explain a refusal, **never** enforced here: one
 * person may hold both codes legitimately while the server still refuses the
 * particular act, and a client that pre-judged it would hide the server's own
 * sentence.
 */
export const PARTY_SOD_PAIRS: readonly (readonly [string, string])[] = [
  [PARTY_PERMISSIONS.mergePropose, PARTY_PERMISSIONS.mergeApprove],
  [PARTY_PERMISSIONS.dsrManage, PARTY_PERMISSIONS.dsrErase],
]
